import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// One lab panel = one blood draw / order. Multiple results live underneath.
export interface LabPanel {
  id: string;
  patient_id: string;
  panel_name: string;
  panel_type: string | null;
  lab_provider: string | null;
  external_order_id: string | null;
  ordered_at: string;
  collected_at: string | null;
  received_at: string | null;
  status: "ordered" | "collected" | "results_received" | "reviewed" | "cancelled";
  pdf_url: string | null;
  clinician_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface LabResult {
  id: string;
  panel_id: string;
  test_code: string;
  test_name: string;
  value: number | null;
  value_text: string | null;
  units: string | null;
  reference_low: number | null;
  reference_high: number | null;
  reference_text: string | null;
  flag: "low" | "high" | "critical_low" | "critical_high" | "abnormal" | null;
  display_order: number | null;
  created_at: string;
}

export interface LabPanelWithResults extends LabPanel {
  lab_results: LabResult[];
  out_of_range_count: number;
}

/** All lab panels for a patient, newest first. Each panel includes its result rows. */
export function useLabPanels(patientId: string | undefined) {
  return useQuery<LabPanelWithResults[]>({
    queryKey: ["labPanels", patientId],
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error } = await supabase
        .from("lab_panels")
        .select("*, lab_results(*)")
        .eq("patient_id", patientId)
        .order("received_at", { ascending: false, nullsFirst: false })
        .order("ordered_at", { ascending: false });
      if (error) throw error;
      // Sort each panel's results by display_order, then count out-of-range
      return (data ?? []).map((p) => {
        const results = (p.lab_results ?? []).slice().sort((a, b) => {
          const ao = a.display_order ?? 999;
          const bo = b.display_order ?? 999;
          return ao - bo;
        });
        const outOfRange = results.filter((r) => r.flag != null).length;
        return { ...p, lab_results: results, out_of_range_count: outOfRange } as LabPanelWithResults;
      });
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}

/** Single panel by id — used on the detail page. */
export function useLabPanel(panelId: string | undefined) {
  return useQuery<LabPanelWithResults | null>({
    queryKey: ["labPanel", panelId],
    queryFn: async () => {
      if (!panelId) return null;
      const { data, error } = await supabase
        .from("lab_panels")
        .select("*, lab_results(*)")
        .eq("id", panelId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const results = (data.lab_results ?? []).slice().sort((a, b) => {
        const ao = a.display_order ?? 999;
        const bo = b.display_order ?? 999;
        return ao - bo;
      });
      const outOfRange = results.filter((r) => r.flag != null).length;
      return { ...data, lab_results: results, out_of_range_count: outOfRange } as LabPanelWithResults;
    },
    enabled: !!panelId,
    staleTime: 60_000,
  });
}

/** Map a status string to a StatusBadge tone + label for consistent rendering. */
export function panelStatusBadge(status: LabPanel["status"]): {
  tone: "active" | "pending" | "muted" | "warn" | "neutral";
  label: string;
} {
  switch (status) {
    case "ordered":          return { tone: "pending", label: "Ordered" };
    case "collected":        return { tone: "pending", label: "Sample collected" };
    case "results_received": return { tone: "active",  label: "Results in" };
    case "reviewed":         return { tone: "active",  label: "Reviewed by clinician" };
    case "cancelled":        return { tone: "warn",    label: "Cancelled" };
    default:                 return { tone: "muted",   label: status };
  }
}
